





























import { Component, Ref, Vue as VueDecorator } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
  },
})
export default class TextInputDialogComponent extends VueDecorator {
  input = "";
  message = "";
  label = "";
  inputType = "";

  /**
   * `null`, not `undefined`: vue-class-component drops `undefined` class properties when it builds
   * `data()`, which would leave this non-reactive. A `null` renders as no `min` attribute at all.
   */
  min: number | null = null;

  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  /**
   * The caller's validation error for what is currently typed, or `null` when the value is
   * acceptable. Derived rather than only checked on save, so an out-of-range value disables SICHERN
   * instead of the field rewriting itself while the user types: clamping each keystroke to `min`
   * made every value starting below it unreachable (typing "300" into a min-20 field turned "3"
   * into "20", then "200").
   */
  get validationError(): string | null {
    // Read `input` FIRST and unconditionally. A computed that returns before touching a reactive
    // property registers no dependency on it, and Vue then caches that result forever: an early
    // `return null` here left SICHERN permanently enabled, because this getter first ran before the
    // dialog was ever opened (no validator set yet) and never re-evaluated afterwards.
    const input = this.input;

    return this.inputValidation ? this.inputValidation(input) ?? null : null;
  }

  get errorMessages(): Array<string> {
    return this.validationError ? [this.validationError] : [];
  }

  /** Blocks the minus sign, so a negative number cannot be typed into a numeric field. */
  onKeyDown(event: KeyboardEvent) {
    if (this.inputType === "number" && event.key === "-") {
      event.preventDefault();
    }
  }

  resolve?: (value: string) => void = undefined;
  reject?: (reason: string) => void = undefined;

  /** `null` rather than `undefined` so it lands in `data()` and stays reactive (see `min`). */
  inputValidation: null | ((input: string) => string | null) = null;

  onSave() {
    // SICHERN is disabled while invalid; this only guards a programmatic call.
    if (this.validationError) {
      return;
    }

    this.baseDialogComponent.isShowing = false;
    this.resolve && this.resolve(this.input);
  }

  async show(
    initialInput: string | undefined,
    title: string,
    message: string,
    label: string,
    inputValidation: null | ((input: string) => string | null),
    inputType?: string,
    min?: number
  ) {
    this.label = label;
    this.input = initialInput ?? "";
    this.message = message;
    this.inputValidation = inputValidation;
    this.inputType = inputType ?? "text";
    this.min = min ?? null;

    this.baseDialogComponent.show({
      title: title,
      positiveButtonText: this.$t("common.save").toString(),
      closeOnSave: false,
    });

    return new Promise<string>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }
}
